/*
 *  ************************************************************************
 *  *  © [2015 - 2020] Quintype Technologies India Private Limited
 *  *  All Rights Reserved.
 *  *************************************************************************
 */
import React from "react";
import {
  ResponsiveHeroImage,
  LoadMoreButton,
  ReviewRating,
  ResponsiveImage,
  LazyLoadImages
} from "@quintype/components";
import { CustomLink } from "../../custom-link";
import TimeAgo from "react-timeago";

import { ImageFallback } from "../../basic/image-fallback";
import format from "date-fns/format";
import get from "lodash/get";
import { isExternalStory, updateStoriesWithAlternatives, getStoryUrl } from "../../../utils";
import PropTypes from "prop-types";
import { SvgHandler } from "../../svg-handler/svg-handler";
import twitterIcon from "../../../../assets/icons/twitter.svg";
import "./author-page.module.css";

function formatter(value, unit, suffix, date, defaultFormatter) {
  return format(new Date(date), "dd MMM, yyyy 'at' HH:mm A");
}

const GetAuthorImage = ({ author }) => {
  const { name = "", "avatar-url": avatarUrl, "avatar-s3-key": avatarS3Key } = author;
  if (!avatarS3Key && !avatarUrl) return null;

  return (
    <div styleName="author-detail__avatar">
      <figure>
        {avatarS3Key ? (
          <LazyLoadImages>
            <ResponsiveImage
              slug={avatarS3Key}
              aspect-ratio={[1, 1]}
              defaultWidth={400}
              imgParams={{ auto: ["format", "compress"] }}
              alt={name}
            />
          </LazyLoadImages>
        ) : (
          <img src={avatarUrl} alt={name} loading="lazy" />
        )}
      </figure>
    </div>
  );
};
GetAuthorImage.propTypes = {
  author: PropTypes.shape({ "avatar-url": PropTypes.string, "avatar-s3-key": PropTypes.string, name: PropTypes.string })
};

export const AuthorDetails = ({ author }) => {
  return (
    <section styleName="author-detail">
      <GetAuthorImage author={author} />
      <div styleName="author-detail__content">
        <h1 styleName="author-detail__name">{author.name}</h1>
        {author.bio && <div styleName="author-detail__bio" dangerouslySetInnerHTML={{ __html: author.bio }} />}
        <div styleName="author-detail__social">
          {author["twitter-handle"] && (
            <a href={"http://twitter.com/" + author["twitter-handle"]} target="_blank" rel="noopener noreferrer">
              <SvgHandler width="24px" height="24px" clazzName="twitter" xlinkHref={twitterIcon} />
            </a>
          )}
        </div>
      </div>
    </section>
  );
};
AuthorDetails.propTypes = {
  author: PropTypes.object
};

export const AuthorMainStories = props => {
  const stories = props.stories || [];
  return (
    <section styleName="author-main-stories">
      {stories.slice(0, 3).map((storyData = {}) => {
        const { story = storyData } = storyData; // because of inconsistent api response structure
        const isReviewStory = story["story-template"] === "review";
        const isVideoStory = story["story-template"] === "video";
        return (
          <div styleName="author-main-stories__story" key={story.id}>
            <CustomLink
              href={"" + getStoryUrl(story)}
              externalLink={isExternalStory(story)}
              styleName="author-main-stories__story__link"
            >
              <div styleName="author-main-stories__story__avatar">
                {story["hero-image-s3-key"] ? (
                  <figure
                    styleName={`author-main-stories__story-image ${
                      isVideoStory ? "author-main-stories__story-image--video" : ""
                    }`}
                  >
                    <ResponsiveHeroImage
                      story={story}
                      aspectRatio={[9, 3]}
                      defaultWidth={480}
                      widths={[300, 400, 500]}
                      sizes="(max-width: 500px) 98%, (max-width: 768px) 48%, 23%"
                      imgParams={{ auto: ["format", "compress"], fit: "max" }}
                    />
                  </figure>
                ) : (
                  <ImageFallback story={story} />
                )}
              </div>
              <div styleName="author-main-stories__story__content">
                <div styleName="author-main-stories__story-headline-rating-wrapper">
                  <h3
                    styleName="author-main-stories__story-headline"
                    dangerouslySetInnerHTML={{ __html: story.headline }}
                  />
                  {isReviewStory && <ReviewRating value={get(story, ["metadata", "review-rating", "value"], "0")} />}
                </div>
                {story["published-at"] && (
                  <p styleName="author-main-stories__story-published-at">
                    Posted &nbsp;
                    <TimeAgo date={story["published-at"]} title={null} formatter={formatter} />
                  </p>
                )}
              </div>
            </CustomLink>
          </div>
        );
      })}
    </section>
  );
};
AuthorMainStories.propTypes = {
  stories: PropTypes.array
};

export const AuthorStories = ({ stories, onLoadMore, noMoreStories }) => {
  const updatedStoriesWithAlternatives = updateStoriesWithAlternatives(stories);
  return (
    <section styleName="author-loadmore-stories">
      {updatedStoriesWithAlternatives.slice(3).map(item => {
        let story = item.story || item;
        const isVideoStory = story["story-template"] === "video";
        return (
          <div styleName="author-loadmore-stories__story" key={story.id}>
            <CustomLink
              href={"" + getStoryUrl(story)}
              externalLink={isExternalStory(story)}
              styleName="author-loadmore-stories__story__link"
            >
              <div styleName="author-loadmore-stories__story__avatar">
                {story["hero-image-s3-key"] ? (
                  <figure
                    styleName={`author-loadmore-stories__story-image ${
                      isVideoStory ? "author-loadmore-stories__story-image--video" : ""
                    }`}
                  >
                    <ResponsiveHeroImage
                      story={story}
                      aspectRatio={[4, 3]}
                      defaultWidth={480}
                      widths={[300, 400, 500]}
                      sizes="(max-width: 500px) 98%, (max-width: 768px) 48%, 23%"
                      imgParams={{ auto: ["format", "compress"], fit: "max" }}
                    />
                  </figure>
                ) : (
                  <ImageFallback story={story} />
                )}
              </div>
              <div styleName="author-loadmore-stories__story__content">
                <h3
                  styleName="author-loadmore-stories__story-heading"
                  dangerouslySetInnerHTML={{ __html: story.headline }}
                />
                {story["published-at"] && (
                  <p styleName="author-main-stories__story-published-at">
                    Posted <TimeAgo date={story["published-at"]} title={null} formatter={formatter} />
                  </p>
                )}
              </div>
            </CustomLink>
          </div>
        );
      })}
      {!noMoreStories && (
        <div styleName="load-more-stories ">
          <LoadMoreButton className="load-more-button" onLoadMore={onLoadMore} text="Load More" />
        </div>
      )}
    </section>
  );
};
AuthorStories.propTypes = {
  stories: PropTypes.array,
  onLoadMore: PropTypes.func,
  noMoreStories: PropTypes.bool
};
